import classNames from "classnames"
import { Children, cloneElement, isValidElement, useRef } from "react"
import { Button } from "../Button"
import { Text } from "../Text"
import { FileUploadItem } from "./FileUploadItem"
import { WRAPPER_CLASSES, WRAPPER_SM_CLASSES } from "../utils/formComponentWrapperClasses"
import type { FileUploadProps } from "./types"

export function FileUpload({
  id,
  label,
  placeholder = "Choose file(s)",
  children,
  disabled,
  error,
  startAdornment,
  endAdornment,
  size = "md",
  "data-analytics-name": dataAnalyticsName,
  ...rest
}: FileUploadProps) {
  const input = useRef<HTMLInputElement>(null)
  const handleClick = () => {
    input.current?.click()
  }

  const isSM = size === "sm"
  const isMD = size === "md"

  const _className = classNames(WRAPPER_CLASSES, {
    "gap-2": isMD,
    [WRAPPER_SM_CLASSES]: isSM,
  })

  const _label =
    label && isValidElement(label)
      ? cloneElement(label, { htmlFor: id, size, ...label.props })
      : label

  return (
    <div className={_className}>
      {_label}
      <Button
        type="button"
        disabled={disabled}
        variation="secondary"
        startAdornment={startAdornment}
        endAdornment={endAdornment}
        size={size}
        onClick={handleClick}
        data-analytics-name={dataAnalyticsName}
      >
        {placeholder}
      </Button>
      <input
        id={id}
        data-testid="file-upload--input"
        className="hidden"
        type="file"
        ref={input}
        // Clear value forcibly so that change event is forced to happen. Fixes bug when uploading same file twice. See https://stackoverflow.com/a/30357800 for more info.
        onClick={e => (e.currentTarget.value = "")}
        {...rest}
      />
      {!!children && Children.count(children) > 0 && (
        <ul className="mt-1 divide-y rounded-md border bg-white">{children}</ul>
      )}
      {!!error && <Text className="text-sm text-error-base">{error}</Text>}
    </div>
  )
}

FileUpload.Item = FileUploadItem
